// Interface
// Base
import { CoreScenarioModule } from "../CoreScenarioModule";
// Actions
import { ReportValidBucketsAction, ReportValidCustomFieldDefinitionsAction, ReportValidGoalsAction } from "@ms/planner-actions/lib/local/dataIntegrity/DataIntegrityActions";
// Constants
import { DefaultTaskGridColumnOrder, GridViewName, TaskGridColumnId } from "@ms/uno-constants/lib/local/TaskGridConstants";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
// Logic modules
import { LogicModule } from "../LogicModule";
import { isCustomFieldColumn, toCustomFieldColumnId } from "@ms/planner-models/lib/local/utilities/CustomFieldDefinitionUtilities";
// Stores
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Utilities
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
import { toViewIdString, updateViewSettings } from "@ms/uno-utilities/lib/local/ViewUtilities";
/**
 * Scenario module for data integrity operations
 */ export class DataIntegrityScenarioModule extends CoreScenarioModule {
    async reportValidBucketsAsync(planId, buckets) {
        await this.dispatcher.dispatchActionAsync(new ReportValidBucketsAction(planId, buckets));
    }
    async reportValidCustomFieldDefinitionsAsync(planId, customFieldDefinitions) {
        await this.dispatcher.dispatchActionAsync(new ReportValidCustomFieldDefinitionsAction(planId, customFieldDefinitions));
        // Synchronize grid column settings with valid custom field definitions
        await this.synchronizeCfdColumns(planId, customFieldDefinitions);
    }
    async reportValidGoalsAsync(planId, goals) {
        await this.dispatcher.dispatchActionAsync(new ReportValidGoalsAction(planId, goals));
    }
    /**
     * Synchronize grid column order and hidden columns with valid custom field definitions:
     * - Remove orphaned CFD IDs that no longer exist
     * - Append new CFD IDs that are not yet in the grid column order
     * Then persist the updated view settings via the roaming service.
     */ async synchronizeCfdColumns(planId, validCustomFieldDefinitions) {
        const viewStore = this.stores.load(Store.View);
        const userStore = this.stores.load(Store.User);
        const viewId = {
            name: ViewName.PlanPage,
            partitionKey: planId
        };
        const savedGridColumns = viewStore.getGridColumnOrder(viewId);
        const hiddenColumns = viewStore.getHiddenGridColumns(viewId);
        if (savedGridColumns == null && hiddenColumns.length === 0) {
            return;
        }
        // Use saved column order, or fall back to the default plan grid column order
        const gridColumns = savedGridColumns ?? [
            ...DefaultTaskGridColumnOrder[GridViewName.PlanTasksGridView]
        ];
        const validPermanentCustomFieldDefinitions = validCustomFieldDefinitions.filter((cfd)=>!LocalIdGenerator.isLocalTempId(cfd.id));
        const validCfdColumnIds = new Set(validPermanentCustomFieldDefinitions.map((cfd)=>toCustomFieldColumnId(cfd.id)));
        const knownColumnIds = new Set(Object.values(TaskGridColumnId));
        // A column is valid if it's a known TaskGridColumnId OR a valid CFD column ID (cf_ prefixed)
        const isValidColumn = (columnId)=>knownColumnIds.has(columnId) || isCustomFieldColumn(columnId) && validCfdColumnIds.has(columnId);
        // Remove orphaned CFD column IDs from both arrays
        const cleanedGridColumns = gridColumns.filter(isValidColumn);
        const cleanedHiddenColumns = hiddenColumns.filter(isValidColumn);
        // Find new CFD column IDs not present in either grid columns or hidden columns and append to grid columns
        const existingColumnIds = new Set([
            ...cleanedGridColumns,
            ...cleanedHiddenColumns
        ]);
        const newCfdColumnIds = validPermanentCustomFieldDefinitions.map((cfd)=>toCustomFieldColumnId(cfd.id)).filter((columnId)=>!existingColumnIds.has(columnId));
        const finalGridColumns = [
            ...cleanedGridColumns,
            ...newCfdColumnIds
        ];
        const gridColumnsChanged = finalGridColumns.length !== gridColumns.length || !gridColumns.every((c, i)=>c === finalGridColumns[i]);
        const hiddenColumnsChanged = cleanedHiddenColumns.length !== hiddenColumns.length;
        if (!gridColumnsChanged && !hiddenColumnsChanged) {
            return;
        }
        const currentRoamingData = userStore.getUserRoamingData().data;
        if (!currentRoamingData) {
            return;
        }
        const viewIdKey = toViewIdString(viewId);
        let updatedViewSettings = currentRoamingData.viewSettings;
        if (gridColumnsChanged) {
            updatedViewSettings = updateViewSettings(updatedViewSettings, viewIdKey, {
                gridColumns: finalGridColumns
            });
        }
        if (hiddenColumnsChanged) {
            updatedViewSettings = updateViewSettings(updatedViewSettings, viewIdKey, {
                hiddenColumns: cleanedHiddenColumns
            });
        }
        const updatedRoamingData = currentRoamingData.setProperty("viewSettings", updatedViewSettings);
        const roamingModule = await this.moduleLoader.load(LogicModule.Roaming);
        await roamingModule.updateRoamingUserDataAsync(updatedRoamingData);
    }
}
